import { useEffect, useRef } from 'react';

/**
 * Custom hook to set up an interval that calls a function repeatedly.
 * Handles setup and cleanup correctly, and uses a ref to ensure the
 * latest callback function is always used without resetting the interval.
 *
 * @param {() => void} callback The function to call on each interval.
 * @param {number | null} delay The interval delay in milliseconds. Set to null to pause the interval.
 */
export function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef<() => void>();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      if (savedCallback.current) {
        savedCallback.current();
      }
    }

    if (delay !== null && delay > 0) {
      const id = setInterval(tick, delay);

      return () => clearInterval(id);
    }

    return undefined;
  }, [delay]);
}
